"use client";

import { useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";
import Image from "next/image";

import { LazyMotion, MotionDiv } from "@/components/animate";
import { cn } from "@/lib/utils";

import { navPreviewItem, navPreviewMotionProps } from "./nav-preview-motion";
import { NavPreviewShell } from "./nav-preview-shell";

/** Figma About nav preview overlay (7253:48724) offset within image (7244:31422). */
const OVERLAY_OFFSET = { x: -82.48, y: -9.17 } as const;

type ArcLine = {
  src: string;
  left: number;
  top: number;
  width: number;
  height: number;
  innerWidth?: number;
  innerHeight?: number;
  rotate?: number;
};

/** Arc vectors from Figma 7253:48735–48760; rotated lines use inner wrapper sizing. */
const ARC_LINES: ArcLine[] = [
  {
    src: "/images/nav/about-preview/vector-3318.svg",
    left: -67.52,
    top: 44.2,
    width: 670.72,
    height: 159.39,
    innerWidth: 665.24,
    innerHeight: 86.46,
    rotate: -6.34,
  },
  {
    src: "/images/nav/about-preview/vector-3317.svg",
    left: -38.23,
    top: 134.41,
    width: 641.41,
    height: 51.86,
  },
  {
    src: "/images/nav/about-preview/vector-3319.svg",
    left: -15.82,
    top: 166.11,
    width: 593.58,
    height: 104.14,
  },
  {
    src: "/images/nav/about-preview/vector-3320.svg",
    left: 25.51,
    top: 200.07,
    width: 511.92,
    height: 133.57,
    innerWidth: 508.08,
    innerHeight: 115.22,
    rotate: -2.08,
  },
];

type LocationMarker = {
  key: "dublin" | "london" | "newYork" | "sydney" | "charlotte" | "remote";
  /** Dot center in overlay container coordinates (Figma ellipse center). */
  dotCenter: { x: number; y: number };
  labelPosition: "above" | "below";
  labelGap: number;
  dotSrc?: string;
  compact?: boolean;
};

/** Dot centers snapped to arc-line intersections, with sub-pixel nudges for image scaling. */
const LOCATION_MARKERS: LocationMarker[] = [
  {
    key: "dublin",
    dotCenter: { x: 144.6, y: 99.5 },
    labelPosition: "above",
    labelGap: 10.5,
  },
  {
    key: "london",
    dotCenter: { x: 212.11, y: 134.5 },
    labelPosition: "above",
    labelGap: 14,
  },
  {
    key: "newYork",
    dotCenter: { x: 419.09, y: 141.5 },
    labelPosition: "above",
    labelGap: 12.5,
    dotSrc: "/images/nav/about-preview/ellipse-dot-alt.svg",
  },
  {
    key: "sydney",
    dotCenter: { x: 139.21, y: 201.0 },
    labelPosition: "below",
    labelGap: 11.5,
  },
  {
    key: "charlotte",
    dotCenter: { x: 362.26, y: 209.5 },
    labelPosition: "below",
    labelGap: 12.5,
  },
  {
    key: "remote",
    dotCenter: { x: 43.46, y: 240.0 },
    labelPosition: "above",
    labelGap: 5,
    dotSrc: "/images/nav/about-preview/ellipse-dot-small.svg",
    compact: true,
  },
];

function overlayPos(x: number, y: number) {
  return {
    left: x + OVERLAY_OFFSET.x,
    top: y + OVERLAY_OFFSET.y,
  };
}

export function CompanyAboutNavPreview() {
  const t = useTranslations("Nav.preview.about");
  const reduceMotion = useReducedMotion();
  const motionProps = navPreviewMotionProps(reduceMotion);

  return (
    <NavPreviewShell>
      <Image
        src="/images/nav/about-preview/about-gradient-bg@2x.png"
        alt=""
        fill
        className="object-cover object-left-top"
        sizes="400px"
        priority
      />

      <LazyMotion>
        <MotionDiv
          className="absolute inset-0 overflow-visible"
          {...motionProps}
        >
          {ARC_LINES.map((line) => (
            <ArcLineGraphic
              key={line.src}
              line={line}
              reduceMotion={reduceMotion}
            />
          ))}

          {LOCATION_MARKERS.map((marker) => (
            <AboutLocationMarker
              key={marker.key}
              label={t(`locations.${marker.key}`)}
              marker={marker}
              reduceMotion={reduceMotion}
            />
          ))}
        </MotionDiv>
      </LazyMotion>
    </NavPreviewShell>
  );
}

function ArcLineGraphic({
  line,
  reduceMotion,
}: {
  line: ArcLine;
  reduceMotion: boolean | null;
}) {
  const pos = overlayPos(line.left, line.top);

  const image = (
    <Image
      src={line.src}
      alt=""
      fill
      unoptimized
      className="max-w-none object-fill"
      aria-hidden
    />
  );

  return (
    <MotionDiv
      className="pointer-events-none absolute"
      style={{
        left: pos.left,
        top: pos.top,
        width: line.width,
        height: line.height,
      }}
      variants={reduceMotion ? undefined : navPreviewItem}
    >
      {line.rotate != null ? (
        <div className="flex size-full items-center justify-center">
          <div
            className="relative shrink-0"
            style={{
              width: line.innerWidth ?? line.width,
              height: line.innerHeight ?? line.height,
              transform: `rotate(${line.rotate}deg)`,
            }}
          >
            {image}
          </div>
        </div>
      ) : (
        <div className="relative size-full">{image}</div>
      )}
    </MotionDiv>
  );
}

function AboutLocationMarker({
  label,
  marker,
  reduceMotion,
}: {
  label: string;
  marker: LocationMarker;
  reduceMotion: boolean | null;
}) {
  const pos = overlayPos(marker.dotCenter.x, marker.dotCenter.y);
  const dotSrc = marker.dotSrc ?? "/images/nav/about-preview/ellipse-dot.svg";
  const dotRadius = marker.compact ? 4 : 5.5;

  return (
    <MotionDiv
      className="pointer-events-none absolute"
      style={{ left: pos.left, top: pos.top }}
      variants={reduceMotion ? undefined : navPreviewItem}
    >
      <AboutLocationDot
        src={dotSrc}
        compact={marker.compact}
        className="absolute -translate-x-1/2 -translate-y-1/2"
      />

      <span
        className={cn(
          "text-grey-0 absolute left-1/2 -translate-x-1/2 text-center leading-[1.4] tracking-[-0.02em] whitespace-nowrap",
          marker.compact ? "text-[10px]" : "text-[14px]"
        )}
        style={
          marker.labelPosition === "above"
            ? { bottom: dotRadius + marker.labelGap }
            : { top: dotRadius + marker.labelGap }
        }
      >
        {label}
      </span>
    </MotionDiv>
  );
}

function AboutLocationDot({
  src,
  compact = false,
  className,
}: {
  src: string;
  compact?: boolean;
  className?: string;
}) {
  const size = compact ? 8 : 11;

  return (
    <div
      className={cn("relative shrink-0", className)}
      style={{ width: size, height: size }}
    >
      <div className="absolute inset-[-44%]">
        <Image
          src={src}
          alt=""
          fill
          unoptimized
          className="max-w-none object-fill"
          aria-hidden
        />
      </div>
    </div>
  );
}
